import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, BookOpen, ExternalLink } from "lucide-react";
import { TRIM_SIZES } from "../components/cover/BookSetupModal";

const BLEED = 0.125;
const PREVIEW_PX_PER_INCH = 48;
const DISPLAY_SCALE = 60;

/**
 * SharePreview — public read-only page for a shared cover.
 * Fetches /api/share/cover/:token (no auth) and renders the assembled
 * full-wrap by stitching all three Fabric.StaticCanvas panels together.
 * Includes the "Created with PublisherKit" footer that doubles as a viral
 * marketing surface.
 */
const SharePreview = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stitchUrl, setStitchUrl] = useState(null);
  const [building, setBuilding] = useState(false);
  const [containerW, setContainerW] = useState(1100);
  const containerRef = useRef(null);
  const [fabricReady, setFabricReady] = useState(typeof window !== "undefined" && !!window.fabric);

  /* ------------------------- Fabric ready ------------------------- */
  useEffect(() => {
    if (window.fabric) { setFabricReady(true); return; }
    const id = setInterval(() => {
      if (window.fabric) { setFabricReady(true); clearInterval(id); }
    }, 100);
    return () => clearInterval(id);
  }, []);

  /* ------------------------- Container width ------------------------- */
  useEffect(() => {
    const update = () => setContainerW(Math.min(window.innerWidth - 48, 1300));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  /* ------------------------- Fetch cover data ------------------------- */
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError("");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/cover/${token}`;
    axios
      .get(url)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        setError(status === 404 ? "not_found" : "load_failed");
      })
      .finally(() => setLoading(false));
  }, [token]);

  /* ------------------------- Build stitched preview ------------------------- */
  useEffect(() => {
    if (!fabricReady || !data) return;
    let cancelled = false;
    setBuilding(true);
    setStitchUrl(null);
    const fabric = window.fabric;
    const trim = TRIM_SIZES.find((t) => t.value === data.trim_size) || TRIM_SIZES[0];
    const trimW = trim.w;
    const trimH = trim.h;
    const spineW = data.spine_width_inches || 0.25;

    const renderPanel = (key) => new Promise((resolve) => {
      const dims = key === "spine"
        ? { trimW: spineW, trimH }
        : { trimW, trimH };
      const wPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
      const hPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
      const el = document.createElement("canvas");
      el.width = Math.round(wPx);
      el.height = Math.round(hPx);
      const c = new fabric.StaticCanvas(el, { width: el.width, height: el.height });
      const json = data[`${key}_canvas_json`];
      const finish = () => {
        c.getObjects().filter((o) => (o.name || "").startsWith("_guide")).forEach((g) => c.remove(g));
        c.renderAll();
        const dataUrl = c.toDataURL({ format: "png", multiplier: PREVIEW_PX_PER_INCH / DISPLAY_SCALE });
        c.dispose();
        resolve(dataUrl);
      };
      if (!json) {
        c.backgroundColor = "#FFFFFF";
        c.renderAll();
        const dataUrl = c.toDataURL({ format: "png", multiplier: PREVIEW_PX_PER_INCH / DISPLAY_SCALE });
        c.dispose();
        resolve(dataUrl);
        return;
      }
      c.loadFromJSON(json, finish);
    });

    const loadImg = (src) => new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

    (async () => {
      try {
        const [backUrl, spineUrl, frontUrl] = await Promise.all([
          renderPanel("back"), renderPanel("spine"), renderPanel("front"),
        ]);
        const totalWidthInches = trimW * 2 + spineW + BLEED * 2;
        const totalHeightInches = trimH + BLEED * 2;
        const totalW = Math.round(totalWidthInches * PREVIEW_PX_PER_INCH);
        const totalH = Math.round(totalHeightInches * PREVIEW_PX_PER_INCH);
        const stitch = document.createElement("canvas");
        stitch.width = totalW;
        stitch.height = totalH;
        const ctx = stitch.getContext("2d");
        const [backImg, spineImg, frontImg] = await Promise.all([
          loadImg(backUrl), loadImg(spineUrl), loadImg(frontUrl),
        ]);
        ctx.drawImage(backImg, 0, 0);
        ctx.drawImage(spineImg, Math.round(trimW * PREVIEW_PX_PER_INCH), 0);
        ctx.drawImage(frontImg, Math.round((trimW + spineW) * PREVIEW_PX_PER_INCH), 0);
        if (cancelled) return;
        setStitchUrl(stitch.toDataURL("image/png"));
      } catch (err) {
        console.error("share preview build failed", err);
      } finally {
        if (!cancelled) setBuilding(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fabricReady, data]);

  /* ------------------------- Render states ------------------------- */
  if (loading) {
    return (
      <FullPage>
        <div className="text-center" data-testid="share-loading">
          <Loader2 className="w-7 h-7 animate-spin text-[#534AB7] mx-auto" />
          <p className="mt-3 text-sm text-[#5F5E5A]">Loading cover…</p>
        </div>
      </FullPage>
    );
  }

  if (error === "not_found") {
    return (
      <FullPage>
        <div className="text-center max-w-md" data-testid="share-not-found">
          <BookOpen className="w-10 h-10 text-[#5F5E5A]/50 mx-auto mb-4" strokeWidth={1.2} />
          <h1 className="text-3xl tracking-tight mb-2" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
            This share link isn't active.
          </h1>
          <p className="text-sm text-[#5F5E5A] leading-relaxed">
            The author may have revoked it, or the link was mistyped.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489]"
          >
            Make your own with PublisherKit
          </Link>
        </div>
      </FullPage>
    );
  }

  if (error) {
    return (
      <FullPage>
        <div className="text-center" data-testid="share-error">
          <p className="text-sm text-[#5F5E5A]">Could not load this preview. Please try again.</p>
        </div>
      </FullPage>
    );
  }

  const trim = TRIM_SIZES.find((t) => t.value === data.trim_size) || TRIM_SIZES[0];
  const trimW = trim.w;
  const trimH = trim.h;
  const spineW = data.spine_width_inches || 0.25;
  const totalWidthInches = trimW * 2 + spineW + BLEED * 2;
  const totalHeightInches = trimH + BLEED * 2;
  const fitW = containerW - 64;
  const scale = Math.min(1, fitW / (totalWidthInches * PREVIEW_PX_PER_INCH));
  const displayW = totalWidthInches * PREVIEW_PX_PER_INCH * scale;
  const displayH = totalHeightInches * PREVIEW_PX_PER_INCH * scale;

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 border-b border-[#D3D1C7] bg-white">
        <div className="max-w-[1300px] mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="share-header-logo">
            <div className="w-8 h-8 rounded-[8px] bg-[#534AB7] flex items-center justify-center group-hover:bg-[#3C3489] transition-colors">
              <BookOpen className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="text-base font-medium text-[#2C2C2A]" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
              PublisherKit
            </span>
          </Link>
          <Link
            to="/signup"
            data-testid="share-header-cta"
            className="text-xs px-3 py-1.5 rounded-[8px] border border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8] inline-flex items-center gap-1.5"
          >
            Make your own → 
          </Link>
        </div>
      </header>

      {/* Title block */}
      <div className="px-6 py-7 text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#5F5E5A] mb-2">
          Cover preview
        </p>
        <h1 className="text-3xl sm:text-4xl tracking-tight text-[#2C2C2A]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }} data-testid="share-title">
          {data.book_title || "Untitled"}
        </h1>
        {data.author && (
          <p className="mt-1.5 text-sm text-[#5F5E5A]">by {data.author}</p>
        )}
        <p className="mt-2 text-[11px] text-[#5F5E5A]/80">
          {trim.label.split("(")[0].trim()} · spine {spineW.toFixed(3)}″
        </p>
      </div>

      {/* Cover preview */}
      <div ref={containerRef} className="flex-1 px-6 pb-8 flex items-start justify-center">
        <div className="bg-[#E8E5DA] rounded-[12px] p-6 sm:p-10" style={{ minHeight: 400 }}>
          {building && (
            <div className="text-center" data-testid="share-building">
              <Loader2 className="w-6 h-6 animate-spin text-[#534AB7] mx-auto" />
              <p className="mt-3 text-xs text-[#5F5E5A]">Stitching panels…</p>
            </div>
          )}
          {!building && stitchUrl && (
            <div
              className="bg-white shadow-[0_30px_70px_-20px_rgba(0,0,0,0.3)]"
              style={{ width: displayW, height: displayH }}
              data-testid="share-preview-image"
            >
              <img src={stitchUrl} alt={`${data.book_title} full-wrap cover`} className="block w-full h-full object-fill" />
            </div>
          )}
        </div>
      </div>

      {/* Marketing footer */}
      <footer className="px-6 py-6 border-t border-[#D3D1C7] bg-white" data-testid="share-footer">
        <div className="max-w-[1300px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5F5E5A]">
          <div>
            Created with{" "}
            <Link to="/" className="text-[#534AB7] font-medium hover:underline">
              PublisherKit
            </Link>{" "}
            — the KDP listing &amp; cover toolkit for self-publishers.
          </div>
          <Link
            to="/signup"
            data-testid="share-footer-cta"
            className="inline-flex items-center gap-1.5 text-[#534AB7] hover:underline font-medium"
          >
            Start your free project <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </footer>
    </div>
  );
};

const FullPage = ({ children }) => (
  <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
    {children}
  </div>
);

export default SharePreview;
